/**
 * PortfolioOverview — colorful, live infographic dashboard for the Basic Plan
 * portal (/projects). Renders KPI cards, portfolio health donut, live activity,
 * open risks and upcoming milestones aggregated across the user's projects.
 * Design matches the "Every project. Live." reference mock — vibrant SaaS
 * palette on a soft purple canvas.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Folder, CheckCircle2, AlertTriangle, Clock, Activity, ShieldAlert,
  CalendarDays, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";

const SEVERITY_STYLES = {
  Critical: "bg-red-100 text-red-700",
  High:     "bg-orange-100 text-orange-700",
  Medium:   "bg-amber-100 text-amber-700",
  Low:      "bg-emerald-100 text-emerald-700",
};

const STATUS_STYLES = {
  "On Track":    "bg-emerald-100 text-emerald-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "At Risk":     "bg-amber-100 text-amber-700",
  "Off Track":   "bg-red-100 text-red-700",
  "Upcoming":    "bg-violet-100 text-violet-700",
  "Delayed":     "bg-red-100 text-red-700",
  "Overdue":     "bg-red-100 text-red-700",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const initials = (s) =>
  (s || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "PM";

/* ── Donut chart ───────────────────────────────────────────────────────── */
function HealthDonut({ segments }) {
  const size = 176;
  const stroke = 26;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  const onTrack = segments.find(s => s.label === "On Track");
  const onTrackPct = onTrack ? Math.round((onTrack.value / total) * 100) : 0;

  return (
    <div className="flex items-center gap-6">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} stroke="#F1F5F9" strokeWidth={stroke} fill="none" />
          {segments.map((seg, i) => {
            const dash = (seg.value / total) * c;
            const el = (
              <circle key={i}
                cx={size/2} cy={size/2} r={r}
                stroke={seg.color} strokeWidth={stroke}
                fill="none"
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="font-display text-3xl font-extrabold text-zinc-900 tracking-tight">{onTrackPct}%</div>
            <div className="text-[10px] font-mono tracking-[0.16em] text-zinc-500 uppercase">On Track</div>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        {segments.map(seg => (
          <div key={seg.label} className="flex items-center gap-2 text-sm" data-testid={`donut-legend-${seg.label.replace(/\s+/g,'-').toLowerCase()}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: seg.color }} />
            <span className="text-zinc-700 flex-1">{seg.label}</span>
            <span className="font-semibold text-zinc-900">{seg.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── KPI tile ──────────────────────────────────────────────────────────── */
function KpiTile({ label, value, sub, Icon, tint, valueClass, testid }) {
  return (
    <div
      className="rounded-2xl bg-white border border-zinc-100 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
      data-testid={testid}
    >
      <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${tint}`}>
        <Icon size={20} strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
        <div className={`font-display text-3xl font-extrabold tracking-tight mt-1 ${valueClass}`}>{value}</div>
        {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

/* ── Portfolio Overview root ───────────────────────────────────────────── */
export default function PortfolioOverview({ ownerName = "there" }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.portfolioOverview()
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message || "Failed to load overview"); });
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/60 p-5 text-sm text-red-700" data-testid="portfolio-overview-error">
        Could not load the portfolio overview: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-2xl border border-zinc-100 bg-white p-8 shimmer text-sm text-zinc-500" data-testid="portfolio-overview-loading">
        Loading portfolio overview…
      </div>
    );
  }

  const k = data.kpis;

  return (
    <section
      className="relative rounded-3xl p-6 md:p-8 mb-8 overflow-hidden anim-in"
      style={{
        background:
          "radial-gradient(1200px 400px at -10% -20%, rgba(139,92,246,0.14) 0%, transparent 60%)," +
          "radial-gradient(900px 380px at 110% 10%, rgba(59,130,246,0.10) 0%, transparent 60%)," +
          "linear-gradient(180deg, #FBFAFF 0%, #F5F3FF 100%)",
        border: "1px solid rgba(139, 92, 246, 0.14)",
      }}
      data-testid="portfolio-overview"
    >
      {/* Decorative dot patterns */}
      <div className="absolute top-3 left-3 grid grid-cols-6 gap-1.5 opacity-30 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-violet-400" />
        ))}
      </div>
      <div className="absolute bottom-3 right-3 grid grid-cols-6 gap-1.5 opacity-25 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-blue-400" />
        ))}
      </div>

      {/* Header banner */}
      <div className="relative flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-600">Capittance Harmonizer</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1 leading-tight">
            <span className="text-violet-700">Every project.</span>{" "}
            <span className="text-blue-600">Live.</span>
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            Welcome back, <span className="font-semibold text-zinc-900">{ownerName}</span>. Here&apos;s what&apos;s happening across your portfolio.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-sm rounded-full bg-white border-violet-200 text-violet-700 hover:bg-violet-50"
          onClick={() => nav("/csuite/dashboard")}
          data-testid="btn-open-csuite-from-overview"
        >
          <Sparkles size={14}/> C-Suite Portal
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 relative">
        <KpiTile
          testid="kpi-total-projects"
          label="Total Projects"
          value={k.total}
          sub="Active projects"
          Icon={Folder}
          tint="bg-violet-100 text-violet-600"
          valueClass="text-zinc-900"
        />
        <KpiTile
          testid="kpi-on-track"
          label="On Track"
          value={k.on_track}
          sub={`${k.on_track_pct}% of projects`}
          Icon={CheckCircle2}
          tint="bg-emerald-100 text-emerald-600"
          valueClass="text-emerald-700"
        />
        <KpiTile
          testid="kpi-at-risk"
          label="At Risk"
          value={k.at_risk}
          sub={`${k.at_risk_pct}% of projects`}
          Icon={AlertTriangle}
          tint="bg-amber-100 text-amber-600"
          valueClass="text-amber-700"
        />
        <KpiTile
          testid="kpi-overdue"
          label="Overdue"
          value={k.overdue}
          sub={k.overdue === 0 ? "No overdue actions" : `Overdue action${k.overdue === 1 ? "" : "s"}`}
          Icon={Clock}
          tint="bg-rose-100 text-rose-600"
          valueClass="text-rose-700"
        />
      </div>

      {/* Row 1: Health donut + Live activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 relative">
        <div className="rounded-2xl bg-white border border-zinc-100 p-6" data-testid="panel-project-health">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Project Health</div>
            <span className="text-[10px] font-mono text-zinc-400">Across all active projects</span>
          </div>
          {k.total === 0 ? (
            <div className="text-sm text-zinc-500 py-10 text-center">No projects yet — create one to light this up.</div>
          ) : (
            <HealthDonut segments={data.health_donut} />
          )}
        </div>

        <div className="rounded-2xl bg-white border border-zinc-100 p-6" data-testid="panel-live-activity">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Live Activity</div>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-600 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
          </div>
          {data.live_activity.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Recent project updates appear here.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.live_activity.map((a, i) => {
                const chipColor = a.health === "On Track" ? "bg-emerald-500" :
                                  a.health === "At Risk"   ? "bg-amber-500" :
                                  a.health === "Off Track" ? "bg-rose-500" : "bg-blue-500";
                const avatarColors = ["bg-violet-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500"];
                return (
                  <li
                    key={i}
                    className="flex items-center gap-3 py-3 cursor-pointer hover:bg-zinc-50 rounded-lg px-2 -mx-2 transition-colors"
                    onClick={() => nav(`/projects/${a.project_id}`)}
                    data-testid={`activity-item-${i}`}
                  >
                    <div className={`w-9 h-9 rounded-full ${avatarColors[i % avatarColors.length]} text-white grid place-items-center text-[11px] font-bold shrink-0`}>
                      {initials(a.pm || a.project)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-900 truncate">{a.project}</div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {a.phase || "In flight"}{a.pm ? ` · PM ${a.pm}` : ""}
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-400 shrink-0">{timeAgo(a.updated)}</div>
                    <span className={`w-2 h-2 rounded-full ${chipColor} shrink-0`} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Row 2: Open risks + Upcoming milestones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 relative">
        <div className="rounded-2xl bg-white border border-zinc-100 p-6" data-testid="panel-open-risks">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-rose-100 grid place-items-center text-rose-600">
                <ShieldAlert size={16} />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Open Risks</div>
                <div className="font-display text-lg font-bold text-zinc-900">
                  {data.open_risks.length} <span className="text-[11px] font-mono text-zinc-500 font-normal">across portfolio</span>
                </div>
              </div>
            </div>
          </div>
          {data.open_risks.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No open risks. 🎉</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.open_risks.map((r, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-zinc-50 rounded-lg px-2 -mx-2 transition-colors"
                  onClick={() => nav(`/projects/${r.project_id}`)}
                  data-testid={`risk-item-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-900 truncate">{r.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.project}</div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full shrink-0 ${SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.Medium}`}>
                    {r.severity}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-zinc-100 p-6" data-testid="panel-upcoming-milestones">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-blue-100 grid place-items-center text-blue-600">
                <CalendarDays size={16} />
              </span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">Upcoming Milestones</div>
                <div className="font-display text-lg font-bold text-zinc-900">
                  Next {data.upcoming_milestones.length} <span className="text-[11px] font-mono text-zinc-500 font-normal">on the horizon</span>
                </div>
              </div>
            </div>
          </div>
          {data.upcoming_milestones.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No milestones scheduled.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.upcoming_milestones.map((m, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-zinc-50 rounded-lg px-2 -mx-2 transition-colors"
                  onClick={() => nav(`/projects/${m.project_id}`)}
                  data-testid={`milestone-item-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-900 truncate">{m.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{m.project}</div>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 shrink-0">{fmtDate(m.date)}</div>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full shrink-0 ${STATUS_STYLES[m.status] || "bg-zinc-100 text-zinc-700"}`}>
                    {m.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Footer tagline */}
      <div className="relative mt-6 pt-6 border-t border-violet-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-zinc-500">
          <Activity size={13} className="text-violet-500" />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Real-time visibility · Faster project decisions</span>
        </div>
        <a href="https://capittance.com" className="text-[11px] font-mono text-violet-600 hover:text-violet-800 tracking-[0.12em]" data-testid="link-capittance">
          CAPITTANCE.COM
        </a>
      </div>
    </section>
  );
}
